import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'

import {ENV} from '../app/Utils/_helpers/EnvHelper'
import {
  ActivateDeactivateSubClientParam,
  AdminRemoveCreditParams,
  AdminReportFilter,
  Affiliate,
  AllocateCreditSubClientParam,
  AllocateCreditSubClientResponse,
  AllocateSubClientBusinessNumberWriteParams,
  AllocateSubClientCustomNumberWriteParams,
  AllocateSubClientDedicatedNumberWriteParams,
  Analytic,
  AnalyticsResponse,
  AppResponse,
  PurchaseProductPlanRequest,
  Automation,
  BulkMatrixParams,
  BulkMatrixResponse,
  CardData,
  Commission,
  CommissionAmount,
  CommissionAmountParams,
  CommissionListParams,
  ConfigureReferralCommissionParams,
  ContactData,
  ContactForm,
  CreateApiTokenParams,
  CreateApiTokenResponse,
  CreditUsage,
  DeleteMultipleTasksPayload,
  CompleteMultipleTasksPayload,
  DeleteContactsParams,
  DeleteContactsResponse,
  DeleteSavedSequenceParams,
  DeleteSubClient,
  DeleteTokenParams,
  DeleteTokenResponse,
  ExportContactResponse,
  ExportContactsParams,
  ExportRawContactList,
  ExportResponse,
  GetApiTokensResponse,
  GetContactCountParams,
  GetContactCountResponse,
  GetContactDetailClickThroughResponse,
  GetContactDetailMessageResponse,
  GetContactDetailResponse,
  GetContactListResponse,
  GetContactParams,
  GetCreditOverviewResponse,
  GetListCountResponse,
  GetListMultiStepAutomationParams,
  GetListMultiStepAutomationResponse,
  GetListParams,
  GetStaggerMessageResponse,
  GetSubClientParam,
  GetSubClientResponse,
  GetSubClientsParam,
  GetSubClientsResponse,
  GetTransactionHistoryParams,
  GetTransactionHistoryResponse,
  LandingPageChart,
  LandingPageShortCode,
  List,
  LoginAsSubClientResponse,
  LoginBackSubClientResponse,
  MessageReportItems,
  MessageReportParams,
  MessageReportResponse,
  PaginateParams,
  PaginateResponse,
  RawContact,
  RawContactSearch,
  SalesAllPurchase,
  SalesGraphData,
  SalesGroupByEnum,
  SalesNewPurchase,
  SalesPurchaseFilter,
  SenderOptions,
  SetWebhookParams,
  SetWebhookResponse,
  ShortLink,
  ShortLinkFilterParam,
  SubClient,
  SubClientBusinessNumberResponse,
  SubClientCustomNumberResponse,
  SubClientDedicatedNumberResponse,
  SubClientSetCreditParams,
  SubClientSetCreditResponse,
  SubClientShareCreditRemainingResponse,
  SubmitReferralCode,
  SubmitReferralCodeApproval,
  TaskApiResponse,
  TaskItem,
  TaskListResponse,
  GetTaskStatsParams,
  TaskStatsResponse,
  TotalConctacts,
  CreateTaskPayload,
  GetTasksParams,
  UpdateTaskRequest,
  UpdateMany,
  UpdatePasswordParam,
  UpdatePasswordResponse,
  UpdateStatusParam,
  UpdateStatusResponse,
  UpdateTokenStatusParams,
  UpdateTokenStatusResponse,
  getTotalSpendCredit,
  PromoAction,
  PromoActionResponse,
  VehicleTypeOption,
  VehicleTypeOptionResponse,
  SavedSequence,
  CardTemplateList,
  CreateWalletCardTemplate,
  Coupons,
  CouponFilter,
  CouponPayload,
  ProductPlanResponse,
  ProductAccessResponse,
  InvoiceListResponse,
  ProductQuotaResponseData,
  ProductSubscriptionResponseData,
  Product,
  UpcomingInvoiceChangePlan,
  CancelReason,
  PaymentMethodCard,
  CreditCheck,
  HustleFollowupSuggestion,
  ActivityLogResponse,
  NotesTimelineResponse,
  CreateNotePayload,
} from './cellcast.v2.types'
import {WalletCardTemplateDetail} from '../app/pages/UserPages/wallet-card-templates/types'
import {CardDataFormSubmit} from '../app/pages/UserPages/wallet-card/type'
import {
  CommissionFilter,
  SalesSummaryReport,
  UserOrderAdminListResponse,
  UserOrderAdminSummaryResponse,
} from './cellcast.type'

import {addQueryParams} from '../app/Utils/_helpers'
import {ApiWalletCardsResponse} from '../app/pages/UserPages/wallet-card/type'
import {
  Flow,
  FlowPaused,
  FlowPayload,
  FlowReport,
  FlowStats,
  FlowStatus,
  FlowTemplateStats,
} from '../app/pages/UserPages/Flow/WorkFlow/FlowTypes'
import {
  IFlowInstance,
  IFlowLog,
  IFlowStatisticsSummary,
  LOGS_STATUS,
} from '../app/Utils/Constant/FlowConstants'
import {VoiceAnalysisResponse, VoiceItem} from '../app/pages/UserPages/contact-collection/type'
import {DefaultMessageResponse, MessageResponse} from './cellcast.type'
import {Node} from 'reactflow'

const API_URL = ENV().REACT_APP_API_V2_URL

export const cellcastV2Api = createApi({
  reducerPath: 'cellcast_api_v2',
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token')
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      return headers
    },
  }),
  endpoints: (builder) => ({
    // contact section
    getContacts: builder.query<GetContactListResponse, GetContactParams>({
      query: (query) => {
        return {
          url: `contact`,
          params: addQueryParams(query),
        }
      },
    }),
    getContactCount: builder.query<GetContactCountResponse, GetContactCountParams>({
      query: (query) => {
        return {
          url: `contact/count`,
          params: addQueryParams(query),
        }
      },
    }),
    exportContact: builder.mutation<ExportContactResponse, ExportContactsParams>({
      query: (params) => ({
        url: 'contact/export',
        method: 'POST',
        params: params.query,
        body: params.body,
      }),
    }),
    deleteContact: builder.mutation<DeleteContactsResponse, DeleteContactsParams>({
      query: (params) => ({
        url: 'contact',
        method: 'DELETE',
        params: params.query,
        body: params.body,
      }),
    }),
    getContactInformation: builder.query<GetContactDetailResponse, string>({
      query: (contactId) => {
        return {
          url: `contact/information`,
          params: {contactId},
        }
      },
    }),
    getContactInformationMessage: builder.query<GetContactDetailMessageResponse, string>({
      query: (contactId) => {
        return {
          url: `contact/information/message`,
          params: {contactId},
        }
      },
    }),
    getContactInformationClickThrough: builder.query<GetContactDetailClickThroughResponse, string>({
      query: (contactId) => {
        return {
          url: `contact/information/click-through`,
          params: {contactId},
        }
      },
    }),

    updateContactFields: builder.mutation<AppResponse<any>, {id: string; fields: string[]}>({
      query: (params) => ({
        url: `mobile/group/${params.id}/fields`,
        method: 'PUT',
        body: {fields: params.fields},
      }),
    }),

    // sub client
    activateSubClient: builder.mutation<AppResponse<null>, ActivateDeactivateSubClientParam>({
      query: (params) => ({
        url: `subClient/activate`,
        method: 'PATCH',
        body: params,
      }),
    }),
    deactivateSubClient: builder.mutation<AppResponse<null>, ActivateDeactivateSubClientParam>({
      query: (params) => ({
        url: `subClient/deactivate`,
        method: 'PATCH',
        body: params,
      }),
    }),
    getSubClients: builder.query<GetSubClientsResponse, GetSubClientsParam>({
      query: (query) => ({
        url: 'subClient/user',
        method: 'GET',
        params: query,
      }),
    }),
    getSubClient: builder.query<GetSubClientResponse, GetSubClientParam>({
      query: ({id}) => ({
        url: 'subClient/user/' + id,
        method: 'GET',
      }),
    }),
    createSubClient: builder.mutation<GetSubClientResponse, FormData>({
      query: (params) => {
        return {
          url: 'subClient/user',
          method: 'POST',
          body: params,
        }
      },
    }),
    updateSubClient: builder.mutation<GetSubClientResponse, {id: string; data: FormData}>({
      query: ({id, data}) => {
        return {
          url: 'subClient/user/' + id,
          method: 'PATCH',
          body: data,
        }
      },
    }),
    deleteSubClient: builder.mutation<GetSubClientResponse, DeleteSubClient>({
      query: (params) => ({
        url: 'subClient/user/' + params.id,
        method: 'DELETE',
      }),
    }),
    updateSubClientPassword: builder.mutation<UpdatePasswordResponse, UpdatePasswordParam>({
      query: (params) => ({
        url: `subClient/user/${params.id}/password`,
        method: 'PATCH',
        body: {
          password: params.password,
        },
      }),
    }),
    updateSubClientStatus: builder.mutation<UpdateStatusResponse, UpdateStatusParam>({
      query: (params) => ({
        url: `subClient/user/${params.id}/status`,
        method: 'PATCH',
        body: {
          status: params.status,
        },
      }),
    }),
    loginAsSubClient: builder.query<LoginAsSubClientResponse, string>({
      query: (id) => ({
        url: `subClient/user/${id}/login`,
        method: 'GET',
      }),
    }),
    loginBackToSubClient: builder.query<LoginBackSubClientResponse, any>({
      query: () => ({
        url: `subClient/backToSubClient`,
        method: 'GET',
      }),
    }),
    getSubClientCreditOverview: builder.query<GetCreditOverviewResponse, string>({
      query: (id) => ({
        url: `subClient/user/${id}/credit`,
        method: 'GET',
      }),
    }),
    allocateCreditSubClient: builder.mutation<
      AllocateCreditSubClientResponse,
      AllocateCreditSubClientParam
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/credit`,
        method: 'POST',
        body: params.data,
      }),
    }),
    getSubClientTransactionHistory: builder.query<
      GetTransactionHistoryResponse,
      GetTransactionHistoryParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/credit-history`,
        method: 'GET',
        params: params.data,
      }),
    }),
    getSubClientSimple: builder.query<AppResponse<SubClient[]>, any>({
      query: () => ({
        url: `subClient/user/all/simple`,
        method: 'GET',
      }),
    }),
    getSubClientTokens: builder.query<GetApiTokensResponse, string>({
      query: (id) => ({
        url: `subClient/user/${id}/token`,
        method: 'GET',
      }),
    }),
    createSubClientTokens: builder.mutation<CreateApiTokenResponse, CreateApiTokenParams>({
      query: (params) => ({
        url: `subClient/user/${params.id}/token`,
        method: 'POST',
        body: params.data,
      }),
    }),
    updateSubClientTokenStatus: builder.mutation<
      UpdateTokenStatusResponse,
      UpdateTokenStatusParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/token`,
        method: 'PATCH',
        body: params.data,
      }),
    }),
    deleteSubClientTokenStatus: builder.mutation<DeleteTokenResponse, DeleteTokenParams>({
      query: (params) => ({
        url: `subClient/user/${params.id}/token`,
        method: 'DELETE',
        body: params.data,
      }),
    }),
    setSubClientWebhook: builder.mutation<SetWebhookResponse, SetWebhookParams>({
      query: (params) => ({
        url: `subClient/user/${params.id}/webhook`,
        method: 'PUT',
        body: params.data,
      }),
    }),
    setSubClientCreditLimit: builder.mutation<SubClientSetCreditResponse, SubClientSetCreditParams>(
      {
        query: (params) => ({
          url: `subClient/user/${params.id}/credit-limit`,
          method: 'PUT',
          body: params.data,
        }),
      }
    ),
    getSubClientRemainingCredit: builder.query<SubClientShareCreditRemainingResponse, any>({
      query: () => ({
        url: `subClient/credit/remaining`,
        method: 'GET',
      }),
    }),

    allocateSubClientDedicatedNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientDedicatedNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/dedicated-number`,
        method: 'PATCH',
        body: {dedicatedNumberId: params.dedicatedNumberId},
      }),
    }),

    removeAllocatedSubClientDedicatedNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientDedicatedNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/dedicated-number`,
        method: 'DELETE',
        body: {dedicatedNumberId: params.dedicatedNumberId},
      }),
    }),

    getAllocatedSubClientDedicatedNumbers: builder.query<
      SubClientDedicatedNumberResponse,
      {id: string}
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/dedicated-number`,
        method: 'GET',
      }),
    }),

    allocateSubClientBusinessNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientBusinessNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/business-number`,
        method: 'PATCH',
        body: {businessNumberId: params.businessNumberId},
      }),
    }),

    removeAllocatedSubClientBusinessNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientBusinessNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/business-number`,
        method: 'DELETE',
        body: {businessNumberId: params.businessNumberId},
      }),
    }),

    getAllocatedSubClientBusinessNumbers: builder.query<
      SubClientBusinessNumberResponse,
      {id: string}
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/business-number`,
        method: 'GET',
      }),
    }),

    allocateSubClientCustomNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientCustomNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/custom-number`,
        method: 'PATCH',
        body: {customNumberId: params.customNumberId},
      }),
    }),

    removeAllocatedSubClientCustomNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      AllocateSubClientCustomNumberWriteParams
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/custom-number`,
        method: 'DELETE',
        body: {customNumberId: params.customNumberId},
      }),
    }),

    getAllocatedSubClientCustomNumbers: builder.query<SubClientCustomNumberResponse, {id: string}>({
      query: (params) => ({
        url: `subClient/user/${params.id}/custom-number`,
        method: 'GET',
      }),
    }),

    // SubClient Default Number Management (Parent Account)
    setSubClientDefaultDedicatedNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {id: string; numberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/dedicated-number/${params.numberId}/setDefault`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    setSubClientDefaultCustomNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {id: string; numberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/custom-number/${params.numberId}/setDefault`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    setSubClientDefaultBusinessNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {id: string; numberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `subClient/user/${params.id}/business-number/${params.numberId}/setDefault`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    // SubClient Self-Management Default Numbers
    setSubClientOwnDefaultDedicatedNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {virtualNumberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `user/subclient/setdefaultdedicatednumber/${params.virtualNumberId}`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    setSubClientOwnDefaultCustomNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {customNumberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `user/subclient/setdefaultcustomnumber/${params.customNumberId}`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    setSubClientOwnDefaultBusinessNumber: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {businessNumberId: string; isDefault: boolean}
    >({
      query: (params) => ({
        url: `user/subclient/setdefaultbusinessnumber/${params.businessNumberId}`,
        method: 'PUT',
        body: {isDefault: params.isDefault},
      }),
    }),

    getCountMessage: builder.query<AppResponse<number>, any>({
      query: () => ({
        url: `gateway/count`,
        method: 'GET',
      }),
    }),

    // contact
    putUploadContacts: builder.mutation({
      query: (params) => ({
        url: `mobile/group/${params.id}`,
        method: 'PUT',
        body: params.data,
      }),
    }),

    putUploadCustomContacts: builder.mutation({
      query: (params) => ({
        url: `mobile/group/uploadCustomContact`,
        method: 'POST',
        body: params.data,
      }),
    }),

    // Summary
    getTotalSpendCredit: builder.query<AppResponse<number>, getTotalSpendCredit>({
      query: (params) => ({
        url: `admin/summary/total-credit`,
        method: 'GET',
        params: params,
      }),
    }),

    // Admin Credit
    adminRemoveCredit: builder.mutation<AppResponse<string>, AdminRemoveCreditParams>({
      query: (params) => ({
        url: `admin/deduct/credit`,
        method: 'POST',
        body: params,
      }),
    }),

    // List
    contactGetList: builder.query<AppResponse<PaginateResponse<List>>, GetListParams>({
      query: (params) => ({
        url: `list`,
        method: 'GET',
        params: params,
      }),
    }),
    contactDetailList: builder.query<AppResponse<List>, string>({
      query: (id) => ({
        url: `list/${id}`,
        method: 'GET',
      }),
    }),
    contactCreateList: builder.mutation<AppResponse<List>, Pick<List, 'name'>>({
      query: (params) => ({
        url: `list`,
        method: 'POST',
        body: params,
      }),
    }),
    contactUpdateList: builder.mutation<AppResponse<List>, Pick<List, 'name' | '_id'>>({
      query: (params) => ({
        url: `list/${params._id}`,
        method: 'PUT',
        body: {name: params.name},
      }),
    }),
    contactDeleteList: builder.mutation<
      AppResponse<{
        deletedCount: number
      }>,
      {ids: string[]}
    >({
      query: (params) => ({
        url: `list`,
        method: 'DELETE',
        body: params,
      }),
    }),
    contactCountList: builder.query<
      GetListCountResponse,
      {
        ids: string[]
      }
    >({
      query: (params) => ({
        url: `list/count`,
        method: 'POST',
        body: params,
      }),
    }),

    //inbox
    inboxReadAll: builder.mutation({
      query: (params) => ({
        url: `gateway/inboxReadAll`,
        method: 'POST',
        body: params,
      }),
    }),

    inboxCloseAll: builder.mutation({
      query: (params) => ({
        url: `gateway/inboxCloseAll`,
        method: 'POST',
        body: params,
      }),
    }),

    generateHustleFollowup: builder.mutation<
      AppResponse<HustleFollowupSuggestion>,
      {contactId: string}
    >({
      query: ({contactId}) => ({
        url: `hustle-actions/followup/${encodeURIComponent(contactId)}`,
        method: 'POST',
      }),
    }),

    // bulk message
    cancelCampaign: builder.mutation<
      AppResponse<{message: string}>,
      {bulkId: string; type?: string}
    >({
      query: ({bulkId, type = 'sms'}) => ({
        url: `gateway/bulk/${bulkId}/cancel?type=${type}`,
        method: 'PATCH',
      }),
    }),

    pauseCampaign: builder.mutation<
      AppResponse<{message: string}>,
      {bulkId: string; type?: string}
    >({
      query: ({bulkId, type = 'sms'}) => ({
        url: `gateway/bulk/${bulkId}/pause?type=${type}`,
        method: 'PATCH',
      }),
    }),

    resumeCampaign: builder.mutation<
      AppResponse<{message: string}>,
      {bulkId: string; type?: string; interval?: number; scheduleAt?: number}
    >({
      query: ({bulkId, type = 'sms', interval, scheduleAt}) => ({
        url: `gateway/bulk/${bulkId}/resume?type=${type}&interval=${interval}&scheduleAt=${scheduleAt}`,
        method: 'PATCH',
      }),
    }),

    // report
    allMessageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message`,
        method: 'GET',
        params: params,
      }),
    }),
    allMessageCreditUsageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/credit/usage`,
        method: 'GET',
        params: params,
      }),
    }),
    getMessageCreditUsageReportExport: builder.query<ExportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/credit/usage/export`,
        method: 'GET',
        params: params,
      }),
    }),

    allMessageReportExport: builder.query<ExportResponse, any>({
      query: (params) => ({
        url: 'report/message/export',
        method: 'GET',
        params: params,
      }),
    }),

    quickMessageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/quick`,
        method: 'GET',
        params: params,
      }),
    }),
    quickApiMessageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/quick-api`,
        method: 'GET',
        params: params,
      }),
    }),
    quickApiMessageCreditUsageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/quick-api-credit-usage`,
        method: 'GET',
        params: params,
      }),
    }),
    emailToSmsReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/email`,
        method: 'GET',
        params: params,
      }),
    }),
    fileSchedulerMessageReport: builder.query<MessageReportResponse, MessageReportParams>({
      query: (params) => ({
        url: `report/message/file-scheduler`,
        method: 'GET',
        params: params,
      }),
    }),
    analyticShortLinkReport: builder.query<
      AppResponse<PaginateResponse<Analytic>>,
      ShortLinkFilterParam
    >({
      query: (params) => ({
        url: `report/short-link/analytic`,
        method: 'GET',
        params: params,
      }),
    }),
    analyticShortLinkReportExport: builder.query<ExportResponse, ShortLinkFilterParam>({
      query: (params) => ({
        url: `report/short-link/analytic/export`,
        method: 'GET',
        params: params,
      }),
    }),
    landingPageShortLinkReport: builder.query<
      AppResponse<PaginateResponse<LandingPageShortCode>>,
      ShortLinkFilterParam
    >({
      query: (params) => ({
        url: `report/short-link/landing-page`,
        method: 'GET',
        params: params,
      }),
    }),
    landingPageShortLinkReportExport: builder.query<ExportResponse, ShortLinkFilterParam>({
      query: (params) => ({
        url: `report/short-link/landing-page/export`,
        method: 'GET',
        params: params,
      }),
    }),
    analyticShortLinkCountByCountry: builder.query<
      AppResponse<
        {
          _id: string
          total: number
          name: string
        }[]
      >,
      null
    >({
      query: () => ({
        url: `report/short-link/analytic/group-by-country/count`,
        method: 'GET',
      }),
    }),
    analyticShortLinkCountByCity: builder.query<
      AppResponse<
        {
          _id: string
          total: number
          name: string
        }[]
      >,
      null
    >({
      query: () => ({
        url: `report/short-link/analytic/group-by-city/count`,
        method: 'GET',
      }),
    }),
    bulkMessageShortLinkBarChart: builder.query<
      AppResponse<
        {
          _id: string
          platform: string
          total: number
        }[]
      >,
      string
    >({
      query: (bulkId) => ({
        url: `report/bulk/${bulkId}/short-link/chart/bar`,
        method: 'GET',
      }),
    }),

    getShortLinks: builder.query<AppResponse<PaginateResponse<ShortLink>>, ShortLinkFilterParam>({
      query: (params) => ({
        url: `tracking-link`,
        method: 'GET',
        params: params,
      }),
    }),

    getShortLinkDetailExport: builder.query<ExportResponse, any>({
      query: (params) => ({
        url: `tracking-link/${params}/export`,
        method: 'GET',
      }),
    }),

    getShortLinksExport: builder.query<ExportResponse, ShortLinkFilterParam>({
      query: (params) => ({
        url: `tracking-link/export`,
        method: 'GET',
        params: params,
      }),
    }),

    sendReportToEmail: builder.query<
      AppResponse<MessageResponse>,
      {bulkId: string; campType: string}
    >({
      query: (params) => ({
        url: `report/bulk/send-email`,
        method: 'GET',
        params,
      }),
    }),

    getAccountTypeChangeRequest: builder.query({
      query: (params) => ({
        url: `admin/admin-approval-account-type-changes`,
        method: 'GET',
        params,
      }),
    }),

    updateAccountTypeChangeRequest: builder.mutation({
      query: (params) => ({
        url: `admin/admin-approval-account-type-changes/${params.id}`,
        method: 'PUT',
        body: params.body,
      }),
    }),

    // multi step automation
    getListMultiStepAutomation: builder.query<
      GetListMultiStepAutomationResponse,
      GetListMultiStepAutomationParams
    >({
      query: (query) => {
        return {
          url: `automation/multi-step`,
          params: query,
        }
      },
    }),
    getDetailMultiStepAutomation: builder.query<AppResponse<Automation>, string>({
      query: (id) => {
        return {
          url: `automation/multi-step/${id}`,
        }
      },
    }),
    createMultiStepAutomation: builder.mutation<AppResponse<Automation>, Automation>({
      query: (payload) => {
        return {
          url: 'automation/multi-step',
          method: 'POST',
          body: payload,
        }
      },
    }),
    updateMultiStepAutomation: builder.mutation<AppResponse<Automation>, Automation>({
      query: (payload) => {
        const {_id, ...data} = payload
        return {
          url: `automation/multi-step/${_id}`,
          method: 'PUT',
          body: data,
        }
      },
    }),
    deleteMultiStepAutomation: builder.mutation<any, string>({
      query: (id) => {
        return {
          url: `automation/multi-step/${id}`,
          method: 'DELETE',
        }
      },
    }),

    updatePostPaidCreditLimit: builder.mutation({
      query: (payload) => {
        const {userId, ...body} = payload
        return {
          url: `admin/update-postpaid-credit-limit/${userId}`,
          method: 'PUT',
          body,
        }
      },
    }),

    updatePostPaidFlateRatePrice: builder.mutation({
      query: (payload) => {
        const {userId, ...body} = payload
        return {
          url: `admin/update-postpaid-flat-rate-price/${userId}`,
          method: 'PUT',
          body,
        }
      },
    }),

    getCreditUsagePerMonth: builder.query<AppResponse<CreditUsage[]>, any>({
      query: (payload) => {
        const {userId, ...params} = payload
        return {
          url: `admin/credit-usage/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),

    // referral
    generateReferralCode: builder.mutation<AppResponse<DefaultMessageResponse>, null>({
      query: () => {
        return {
          url: `referral/generate`,
          method: 'PATCH',
        }
      },
    }),
    submitReferralCode: builder.mutation<AppResponse<DefaultMessageResponse>, SubmitReferralCode>({
      query: (body) => {
        return {
          url: `referral/submit`,
          method: 'POST',
          body: body,
        }
      },
    }),
    submitReferralCodeApproval: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      SubmitReferralCodeApproval
    >({
      query: (body) => {
        return {
          url: `admin/referral/approval`,
          method: 'POST',
          body: body,
        }
      },
    }),
    getAffiliate: builder.query<
      AppResponse<PaginateResponse<Affiliate>>,
      Partial<
        PaginateParams & {
          code?: string
        }
      >
    >({
      query: (params) => {
        return {
          url: `referral/affiliate`,
          method: 'GET',
          params: params,
        }
      },
    }),
    getAffiliateCount: builder.query<
      AppResponse<{total: number}>,
      {
        code?: string
      }
    >({
      query: () => {
        return {
          url: `referral/affiliate/count`,
          method: 'GET',
        }
      },
    }),
    adminGetAffiliate: builder.query<
      AppResponse<PaginateResponse<Affiliate>>,
      PaginateParams & {userId: string}
    >({
      query: (payload) => {
        const {userId, ...params} = payload
        return {
          url: `/admin/referral/affiliate/${userId}`,
          method: 'GET',
          params: params,
        }
      },
    }),
    adminGetAffiliateCount: builder.query<AppResponse<{total: number}>, string>({
      query: (userId) => {
        return {
          url: `/admin/referral/affiliate/${userId}/count`,
          method: 'GET',
        }
      },
    }),
    adminSetCommissionRate: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      ConfigureReferralCommissionParams
    >({
      query: (payload) => {
        const {userId, ...params} = payload
        return {
          url: `admin/referral/commission/${userId}`,
          method: 'PATCH',
          body: params,
        }
      },
    }),
    getCommissions: builder.query<AppResponse<PaginateResponse<Commission>>, CommissionListParams>({
      query: (params) => {
        return {
          url: `referral/commission`,
          method: 'GET',
          params: params,
        }
      },
    }),
    getCommissionAmount: builder.query<AppResponse<CommissionAmount>, CommissionAmountParams>({
      query: (params) => {
        return {
          url: `referral/commission/amount`,
          method: 'GET',
          params: params,
        }
      },
    }),
    adminGetUserCommissions: builder.query<
      AppResponse<PaginateResponse<Commission>>,
      CommissionListParams & {userId: string}
    >({
      query: (payload) => {
        const {userId, ...params} = payload
        return {
          url: `/admin/referral/commission/${userId}`,
          method: 'GET',
          params: params,
        }
      },
    }),
    adminGetUserCommissionAmount: builder.query<
      AppResponse<CommissionAmount>,
      CommissionAmountParams & {userId: string}
    >({
      query: (payload) => {
        const {userId, ...params} = payload
        return {
          url: `/admin/referral/commission/${userId}/amount`,
          method: 'GET',
          params: params,
        }
      },
    }),
    userRequestAccess: builder.mutation<AppResponse<DefaultMessageResponse>, {targetId: string}>({
      query: (payload) => {
        return {
          url: `referral/affiliate/request-access`,
          method: 'PATCH',
          body: payload,
        }
      },
    }),
    affiliateResponseAccess: builder.mutation<
      AppResponse<DefaultMessageResponse>,
      {notificationId: string; status: string}
    >({
      query: (payload) => {
        return {
          url: `referral/affiliate/response-access`,
          method: 'PATCH',
          body: payload,
        }
      },
    }),
    affiliateChangeAccess: builder.mutation<AppResponse<DefaultMessageResponse>, {status: string}>({
      query: (payload) => {
        return {
          url: `referral/affiliate/update-access`,
          method: 'PATCH',
          body: payload,
        }
      },
    }),
    loginAsAffiliateUser: builder.mutation<AppResponse<any>, {targetUserId: string}>({
      query: (payload) => {
        return {
          url: `referral/affiliate/${payload.targetUserId}/login`,
          method: 'POST',
        }
      },
    }),
    loginAsParentAffiliateUser: builder.mutation<AppResponse<any>, any>({
      query: () => {
        return {
          url: `referral/backToAffiliateParent`,
          method: 'POST',
        }
      },
    }),

    changeCreditStatus: builder.mutation<AppResponse<UpdateMany>, any>({
      query: (body) => {
        return {
          url: 'admin/credit-history-change-status',
          method: 'POST',
          body,
        }
      },
    }),

    // fraud keyword
    adminGetFraudKeyword: builder.query<AppResponse<string>, null>({
      query: () => {
        return {
          url: `admin/fraud-keyword`,
          method: 'GET',
        }
      },
    }),
    adminUpdateFraudKeyword: builder.mutation<
      AppResponse<string>,
      {
        keywords: string
      }
    >({
      query: (body) => {
        return {
          url: 'admin/fraud-keyword',
          method: 'PUT',
          body,
        }
      },
    }),

    landingPageReportChart: builder.query<
      AppResponse<LandingPageChart>,
      Pick<LandingPageShortCode, 'bulkId'>
    >({
      query: (params) => {
        return {
          url: 'report/short-link/landing-page/chart',
          method: 'GET',
          params,
        }
      },
    }),

    // senders
    getSenders: builder.query<AppResponse<SenderOptions>, null>({
      query: () => {
        return {
          url: `gateway/senders`,
          method: 'GET',
        }
      },
    }),

    getApiMessageExport: builder.query<ExportResponse, any>({
      query: (params) => ({
        url: 'report/message/quick-api/export',
        method: 'GET',
        params: params,
      }),
    }),

    getNewUserCount: builder.query<AppResponse<TotalConctacts>, any>({
      query: ({userId, ...params}) => ({
        url: `report/new-user-count/${userId}`,
        method: 'GET',
        params,
      }),
    }),

    // bulk message - stagger message
    getStaggerMessages: builder.query<GetStaggerMessageResponse, string>({
      query: (staggerId) => ({
        url: `report/bulk/stagger/${staggerId}`,
        method: 'GET',
      }),
    }),

    // smpp -server
    smppAdminActivate: builder.mutation<AppResponse<null>, string>({
      query: (userId) => {
        return {
          url: `admin/smpp/${userId}/activate`,
          method: 'POST',
        }
      },
    }),

    smppAdminDeactivate: builder.mutation<AppResponse<null>, string>({
      query: (userId) => {
        return {
          url: `admin/smpp/${userId}/deactivate`,
          method: 'POST',
        }
      },
    }),

    smppAdminChangePassword: builder.mutation<
      AppResponse<null>,
      {
        userId: string
        password: string
      }
    >({
      query: (params) => {
        return {
          url: `admin/smpp/${params.userId}/password`,
          method: 'PATCH',
          body: {password: params.password},
        }
      },
    }),

    smppUserChangePassword: builder.mutation<
      AppResponse<null>,
      {
        password: string
      }
    >({
      query: (params) => {
        return {
          url: `smpp/password`,
          method: 'PATCH',
          body: {password: params.password},
        }
      },
    }),

    exportArchiveMessage: builder.query<
      AppResponse<{
        downloadurl: string
      }>,
      {receiver: string}
    >({
      query: ({receiver}) => {
        return {
          url: `report/message/archive/export`,
          method: 'GET',
          params: {
            receiver,
          },
        }
      },
    }),

    isHaveArchiveData: builder.query<AppResponse<boolean>, {receiver: string}>({
      query: ({receiver}) => {
        return {
          url: `report/message/archive/exist`,
          method: 'GET',
          params: {receiver},
        }
      },
    }),

    // tasks
    getTasks: builder.query<TaskApiResponse<TaskListResponse>, GetTasksParams>({
      query: (params) => ({
        url: `tasks`,
        method: 'GET',
        params,
      }),
      keepUnusedDataFor: 300,
    }),
    getTasksByContactId: builder.query<
      TaskApiResponse<TaskListResponse>,
      {contactId: string; page?: number; limit?: number}
    >({
      query: ({contactId, page = 1, limit = 10}) => ({
        url: `tasks/contact/${contactId}`,
        method: 'GET',
        params: {page, limit},
      }),
      keepUnusedDataFor: 300,
    }),
    getTaskById: builder.query<TaskApiResponse<TaskItem>, string>({
      query: (id) => ({
        url: `tasks/${id}`,
        method: 'GET',
      }),
    }),
    createTask: builder.mutation<TaskApiResponse<TaskItem>, CreateTaskPayload>({
      query: (payload) => ({
        url: `tasks`,
        method: 'POST',
        body: payload,
      }),
    }),
    updateTask: builder.mutation<TaskApiResponse<TaskItem>, UpdateTaskRequest>({
      query: ({id, data}) => ({
        url: `tasks/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),
    completeTask: builder.mutation<TaskApiResponse<TaskItem>, string>({
      query: (id) => ({
        url: `tasks/${id}/complete`,
        method: 'PUT',
      }),
    }),
    completeMultipleTasks: builder.mutation<
      TaskApiResponse<{modifiedCount: number}>,
      CompleteMultipleTasksPayload
    >({
      query: (payload) => ({
        url: `tasks/complete-multiple`,
        method: 'POST',
        body: payload,
      }),
    }),
    deleteTask: builder.mutation<TaskApiResponse<{deletedCount: number}>, string>({
      query: (id) => ({
        url: `tasks/${id}`,
        method: 'DELETE',
      }),
    }),
    deleteMultipleTasks: builder.mutation<
      TaskApiResponse<{deletedCount: number}>,
      DeleteMultipleTasksPayload
    >({
      query: (payload) => ({
        url: `tasks/delete-multiple`,
        method: 'POST',
        body: payload,
      }),
    }),
    getTaskStats: builder.query<TaskApiResponse<TaskStatsResponse>, GetTaskStatsParams | undefined>(
      {
        query: (params) => ({
          url: `tasks/stats`,
          method: 'GET',
          params,
        }),
      }
    ),

    // contact form
    getContactForms: builder.query<
      AppResponse<PaginateResponse<ContactForm>>,
      {
        page: number
        limit: number
      }
    >({
      query: (payload) => {
        return {
          url: `contact-form`,
          method: 'GET',
          params: {
            page: payload.page,
            limit: payload.limit,
          },
        }
      },
    }),

    getContactForm: builder.query<AppResponse<ContactForm>, {id: string}>({
      query: (payload) => {
        return {
          url: `contact-form/${payload.id}`,
          method: 'GET',
        }
      },
    }),

    getContactFormActive: builder.query<AppResponse<ContactForm>, {id: string}>({
      query: (payload) => {
        return {
          url: `contact-form/${payload.id}/active`,
          method: 'GET',
        }
      },
    }),

    createContactForm: builder.mutation<AppResponse<ContactForm>, any>({
      query: (payload) => {
        return {
          url: `contact-form`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateContactForm: builder.mutation<AppResponse<ContactForm>, {id: string; data: any}>({
      query: (payload) => {
        return {
          url: `contact-form/${payload.id}`,
          method: 'PUT',
          body: payload.data,
        }
      },
    }),

    deleteContactForm: builder.mutation<AppResponse<ContactForm>, {id: string}>({
      query: (payload) => {
        return {
          url: `contact-form/${payload.id}`,
          method: 'DELETE',
        }
      },
    }),

    getBulkMatrixReport: builder.query<AppResponse<BulkMatrixResponse>, BulkMatrixParams>({
      query: (params) => {
        return {
          url: `report/bulk/matrix`,
          method: 'GET',
          params: params,
        }
      },
    }),

    // staff affiliate code
    createStaffCode: builder.mutation<
      AppResponse<{message: string}>,
      {
        code: string
      }
    >({
      query: (params) => {
        return {
          url: `admin/staff/code`,
          method: 'POST',
          body: params,
        }
      },
    }),

    removeStaffCode: builder.mutation<
      AppResponse<{message: string}>,
      {
        code: string
      }
    >({
      query: (params) => {
        return {
          url: `admin/staff/code`,
          method: 'DELETE',
          body: params,
        }
      },
    }),

    getWalletCardList: builder.query<
      AppResponse<ApiWalletCardsResponse>,
      {page: number; limit: number}
    >({
      query: (payload) => {
        return {
          url: `wallet-cards`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getWalletCard: builder.query<AppResponse<CardData>, {id: string}>({
      query: (payload) => {
        return {
          url: `wallet-cards/${payload.id}`,
          method: 'GET',
        }
      },
    }),

    getWalletCardPublic: builder.query<AppResponse<CardData>, {id: string}>({
      query: (payload) => {
        return {
          url: `/public/wallet-cards/${payload.id}`,
          method: 'GET',
        }
      },
    }),

    createWalletCardDraft: builder.mutation<AppResponse<CardData>, CardDataFormSubmit>({
      query: (payload) => {
        return {
          url: `wallet-cards/draft`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateWalletCardDraft: builder.mutation<AppResponse<CardData>, CardDataFormSubmit>({
      query: (payload) => {
        return {
          url: `wallet-cards/${payload.id}/draft`,
          method: 'PUT',
          body: payload,
        }
      },
    }),

    createWalletCardPublic: builder.mutation<AppResponse<ContactData>, ContactData>({
      query: (payload) => {
        return {
          url: `public/raw-contacts`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateWalletCardPublic: builder.mutation<AppResponse<{data: ContactData}>, ContactData>({
      query: (payload) => {
        return {
          url: `raw-contacts/${payload.id}`,
          method: 'PUT',
          body: payload,
        }
      },
    }),

    downloadWalletCardPublic: builder.query<Blob, {id: string}>({
      query: (payload) => ({
        url: `public/wallet-cards/${payload.id}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    downloadWalletCardPublicGoogle: builder.query<{data: {googleWalletUrl: string}}, {id: string}>({
      query: (payload) => ({
        url: `public/wallet-cards/${payload.id}/google-wallet/download`,
        method: 'GET',
      }),
    }),

    getActivityLogByPhoneNumber: builder.query<
      AppResponse<ActivityLogResponse>,
      {phoneNumber: string; page?: number; limit?: number}
    >({
      query: ({phoneNumber, page = 1, limit = 50}) => ({
        url: `activity-log/by-phone-number/${phoneNumber}`,
        method: 'GET',
        params: {page, limit},
      }),
    }),
    updatePositionStage: builder.mutation<AppResponse<ContactData>, {id: string; position: number}>(
      {
        query: (payload) => ({
          url: `raw-contacts/${payload.id}/position`,
          method: 'PUT',
          body: payload,
        }),
      }
    ),

    // Notes Timeline
    getNotesTimelineByPhoneNumber: builder.query<
      AppResponse<NotesTimelineResponse>,
      {phoneNumber: string; page?: number; limit?: number}
    >({
      query: ({phoneNumber, page = 1, limit = 20}) => ({
        url: `raw-contacts/notes-timeline/${phoneNumber}`,
        method: 'GET',
        params: {page, limit},
      }),
      // Don't cache - always fetch fresh data when phone number changes
      keepUnusedDataFor: 0,
    }),

    createNoteByPhoneNumber: builder.mutation<
      AppResponse<{data: ContactData; message: string}>,
      CreateNotePayload
    >({
      query: ({phoneNumber, noteText}) => ({
        url: `raw-contacts/notes-timeline/${phoneNumber}`,
        method: 'POST',
        body: {noteText},
      }),
    }),

    // Get raw contact by mobile number (for AI summary)
    getRawContactByMobile: builder.query<AppResponse<ContactData>, {phoneNumber: string}>({
      query: ({phoneNumber}) => ({
        url: `raw-contacts/mobile/${phoneNumber}`,
        method: 'GET',
      }),
    }),

    getSavedSequences: builder.query<AppResponse<PaginateResponse<SavedSequence>>, PaginateParams>({
      query: (params) => ({
        url: 'saved-sequences',
        method: 'GET',
        params: {
          _page: params.page,
          limit: params.limit,
        },
      }),
    }),

    deleteSavedSequence: builder.mutation<
      AppResponse<{message: string}>,
      DeleteSavedSequenceParams
    >({
      query: ({id}) => ({
        url: `saved-sequences/${id}`,
        method: 'DELETE',
      }),
    }),

    // flow
    getFlows: builder.query<AppResponse<PaginateResponse<Flow>>, PaginateParams>({
      query: (params) => ({
        url: 'sms-flow',
        method: 'GET',
        params: params,
      }),
    }),

    getFlowStats: builder.query<AppResponse<FlowStats>, void>({
      query: () => ({
        url: 'sms-flow/stats',
        method: 'GET',
      }),
    }),

    createFlow: builder.mutation<AppResponse<Flow>, FlowPayload>({
      query: (payload) => ({
        url: 'sms-flow',
        method: 'POST',
        body: payload,
      }),
    }),

    deleteFlow: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/${id}`,
        method: 'DELETE',
      }),
    }),

    getFlow: builder.query<AppResponse<Flow>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/${id}`,
        method: 'GET',
      }),
    }),

    getFlowPaused: builder.query<AppResponse<FlowPaused>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/${id}/paused`,
        method: 'GET',
      }),
    }),

    resumeFlowPaused: builder.mutation<AppResponse<null>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/${id}/resume-paused`,
        method: 'PUT',
      }),
    }),

    downloadContactPublic: builder.query<Blob, {id: string}>({
      query: (payload) => ({
        url: `public/wallet-cards/${payload.id}/contact/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    getWalletCardTemplate: builder.query<
      AppResponse<CardTemplateList>,
      {page: number; limit: number}
    >({
      query: (payload) => ({
        url: `wallet-card-templates`,
        method: 'GET',
        params: payload,
      }),
    }),

    getWalletCardTemplateDetail: builder.query<AppResponse<WalletCardTemplateDetail>, {id: string}>(
      {
        query: (payload) => ({
          url: `wallet-card-templates/${payload.id}`,
          method: 'GET',
        }),
      }
    ),

    createWalletCardTemplate: builder.mutation<AppResponse<any>, CreateWalletCardTemplate>({
      query: (payload) => ({
        url: `wallet-card-templates`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateWalletCardTemplate: builder.mutation<AppResponse<any>, WalletCardTemplateDetail>({
      query: (payload) => ({
        url: `wallet-card-templates/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteWalletCardTemplate: builder.mutation<AppResponse<any>, {id: string}>({
      query: (payload) => ({
        url: `wallet-card-templates/${payload.id}`,
        method: 'DELETE',
      }),
    }),

    createWalletCardTemplateDraft: builder.mutation<AppResponse<any>, CreateWalletCardTemplate>({
      query: (payload) => ({
        url: `wallet-card-templates/draft`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateWalletCardTemplateDraft: builder.mutation<AppResponse<any>, WalletCardTemplateDetail>({
      query: (payload) => ({
        url: `wallet-card-templates/${payload.id}/draft`,
        method: 'PUT',
        body: payload,
      }),
    }),

    getWalletCardTemplateListSelect: builder.query<AppResponse<WalletCardTemplateDetail[]>, void>({
      query: () => ({
        url: `wallet-card-templates/templates`,
        method: 'GET',
      }),
    }),

    createWalletCard: builder.mutation<AppResponse<CardData>, CardDataFormSubmit>({
      query: (payload) => {
        return {
          url: `wallet-cards`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateWalletCard: builder.mutation<AppResponse<CardData>, CardDataFormSubmit>({
      query: (payload) => {
        return {
          url: `wallet-cards/${payload.id}`,
          method: 'PUT',
          body: payload,
        }
      },
    }),

    deleteWalletCard: builder.mutation<AppResponse<{items: []}>, {id: string}>({
      query: (payload) => {
        return {
          url: `wallet-cards/${payload.id}`,
          method: 'DELETE',
        }
      },
    }),

    getRawContactList: builder.query<AppResponse<{items: RawContact[]}>, RawContactSearch>({
      query: (payload) => {
        return {
          url: `/raw-contacts`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getRawContactDetail: builder.query<AppResponse<ContactData>, {id: string}>({
      query: (payload) => {
        return {
          url: `raw-contacts/${payload.id}`,
          method: 'GET',
        }
      },
    }),
    exportRawContactList: builder.mutation<
      AppResponse<{data: ExportRawContactList}>,
      RawContactSearch
    >({
      query: (payload) => {
        return {
          url: `/raw-contacts/export`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    uploadVoice: builder.mutation<AppResponse<any>, {voiceFile: string; rawContact: string}>({
      query: (payload) => {
        return {
          url: `/voices`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    getRecordList: builder.query<
      AppResponse<VoiceAnalysisResponse>,
      {id: string; page?: number; limit?: number}
    >({
      query: (payload) => {
        const params = new URLSearchParams()
        params.append('rawContact', payload.id)
        if (payload.page) params.append('page', payload.page.toString())
        if (payload.limit) params.append('limit', payload.limit.toString())

        return {
          url: `/voices?${params.toString()}`,
          method: 'GET',
        }
      },
    }),

    deleteVoice: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: (payload) => {
        return {
          url: `/voices/${payload.id}`,
          method: 'DELETE',
        }
      },
    }),

    updateVoice: builder.mutation<
      AppResponse<VoiceItem>,
      {
        id: string
        welcomeMessage?: string
        nextCallOpeners?: string[]
        transcript?: string
        summary?: string
        highlights?: string[]
        actionItems?: string[]
        sentiment?: string
        leadIntent?: string
        status?: string
      }
    >({
      query: (payload) => {
        const {id, ...body} = payload
        return {
          url: `/voices/${id}`,
          method: 'PUT',
          body,
        }
      },
    }),

    createRawContact: builder.mutation<
      AppResponse<{data: RawContact; message: string}>,
      {
        fullName?: string
        phone?: string
        email?: string
        source?: string
        promoSettings?: string[]
        tags?: string[]
        assignedTo?: string
        intent?: string
        noteText?: string
        user: string
        interestedVehicleTypes?: string[]
        interestedVehicleType?: string | null
      }
    >({
      query: (payload) => {
        return {
          url: `/raw-contacts`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    addRawContact: builder.mutation<
      AppResponse<{items: RawContact[]; message: string; status: boolean}>,
      {ids: string[]}
    >({
      query: (payload) => {
        return {
          url: `/raw-contacts/migrate-to-contact`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    deleteRawContact: builder.mutation<
      AppResponse<{data: {deletedCount: string}; message: string}>,
      {ids: string[]}
    >({
      query: (payload) => {
        return {
          url: `/raw-contacts/bulk`,
          method: 'DELETE',
          body: payload,
        }
      },
    }),

    // Update raw contact verification status
    updateRawContactSale: builder.mutation<
      AppResponse<{data: ContactData}>,
      {id: string; sales: boolean}
    >({
      query: (payload) => {
        return {
          url: `raw-contacts/status/${payload.id}`,
          method: 'PUT',
          body: {sales: payload.sales},
        }
      },
    }),

    // Update raw contact position
    updateRawContactPosition: builder.mutation<
      AppResponse<{data: RawContact}>,
      {id: string; stageId: string; rankAbove: string | null; rankBelow: string | null}
    >({
      query: (payload) => {
        return {
          url: `raw-contacts/${payload.id}/position`,
          method: 'PUT',
          body: {
            stageId: payload.stageId,
            rankAbove: payload.rankAbove,
            rankBelow: payload.rankBelow,
          },
        }
      },
    }),

    addTagtoRowContact: builder.mutation<
      AppResponse<{data: {items: RawContact[]}; message: string}>,
      {contactIds: string[]; tagIds: string[]}
    >({
      query: (payload) => {
        return {
          url: `raw-contacts/assign-tags-to-raw-contact`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updateWelcomeMessageSent: builder.mutation<AppResponse<{message: string}>, { id: string, isSentWelcomeMessage: boolean }>({
      query: (payload) => {
        return {
          url: `raw-contacts/welcome-message-sent/${payload.id}`,
          method: 'PUT',
          body: { isSentWelcomeMessage: payload.isSentWelcomeMessage },
        }
      },
    }),

    getWalletCardReport: builder.query<
      AppResponse<AnalyticsResponse>,
      {startDate: string; endDate: string}
    >({
      query: (payload) => {
        return {
          url: `wallet-cards/report`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getWalletCardReportStacked: builder.query<
      AppResponse<{
        stackedBarData: any[]
        dates: string[]
        totalBites: number
        totalBitesGrowth: number
        totalLeads: number
        totalLeadGrowth: number
        totalSales: number
        totalSalesGrowth: number
        totalWarmUp: number
        totalWarmUpGrowth: number
      }>,
      {fromDate: string; toDate: string}
    >({
      query: (payload) => {
        return {
          url: `wallet-cards/report-stacked`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getWalletCardReportChartDetail: builder.query<
      AppResponse<{
        cardMetrics: Record<string, {name: string; type: 'column'; data: number[]}[]>
        dates: string[]
      }>,
      {fromDate: string; toDate: string}
    >({
      query: (payload) => {
        return {
          url: `wallet-cards/report-chart-detail`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getPromoActionList: builder.query<AppResponse<PromoActionResponse>, GetListParams>({
      query: (payload) => {
        return {
          url: `promo-settings`,
          method: 'GET',
          params: payload,
        }
      },
    }),

    getVehicleTypeOptions: builder.query<AppResponse<VehicleTypeOptionResponse>, GetListParams>({
      query: (payload) => ({
        url: `vehicle-type-options`,
        method: 'GET',
        params: payload,
      }),
    }),

    createVehicleTypeOption: builder.mutation<
      AppResponse<VehicleTypeOption>,
      {name: string; description?: string; color?: string}
    >({
      query: (payload) => ({
        url: `vehicle-type-options`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateVehicleTypeOption: builder.mutation<
      AppResponse<VehicleTypeOption>,
      {id: string; name: string; description?: string; color?: string}
    >({
      query: (payload) => ({
        url: `vehicle-type-options/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteVehicleTypeOption: builder.mutation<AppResponse<{deletedCount: number}>, {ids: string[]}>(
      {
        query: (payload) => ({
          url: `vehicle-type-options`,
          method: 'DELETE',
          body: payload,
        }),
      }
    ),

    getVehicleTypeOptionsPublic: builder.query<
      AppResponse<VehicleTypeOptionResponse>,
      {
        userId: string
        page?: number
        limit?: number
        search?: string
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
      }
    >({
      query: (params) => ({
        url: `public/vehicle-type-options`,
        method: 'GET',
        params,
      }),
    }),

    addPromoAction: builder.mutation<AppResponse<PromoAction>, {name: string}>({
      query: (payload) => {
        return {
          url: `promo-settings`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    updatePromoAction: builder.mutation<AppResponse<PromoAction>, {id: string; name: string}>({
      query: (payload) => {
        return {
          url: `promo-settings/${payload.id}`,
          method: 'PUT',
          body: payload,
        }
      },
    }),

    // sales
    getSalesNewUserCountInRange: builder.query<
      AppResponse<{
        totalUserPurchase30day: number
        totalUserPurchase60day: number
        totalUserPurchase90day: number
        totalUserPurchase120day: number
      }>,
      any
    >({
      query: () => {
        return {
          url: `admin/sales/new-purchase/user-count-in-range`,
          method: 'GET',
        }
      },
    }),

    getSalesNewPurchase: builder.query<
      AppResponse<PaginateResponse<SalesNewPurchase>>,
      SalesPurchaseFilter
    >({
      query: (params) => {
        return {
          url: `admin/sales/new-purchase`,
          method: 'GET',
          params: params,
        }
      },
    }),

    getSalesAllPurchase: builder.query<
      AppResponse<PaginateResponse<SalesAllPurchase>>,
      SalesPurchaseFilter
    >({
      query: (params) => {
        return {
          url: `admin/sales/all-purchase`,
          method: 'GET',
          params: params,
        }
      },
    }),

    exportSalesAllPurchase: builder.query<AppResponse<{downloadurl: string}>, any>({
      query: (params) => {
        return {
          url: `admin/sales/all-purchase/export`,
          method: 'GET',
          params: params,
        }
      },
    }),

    getSalesNewUserCount: builder.query<AppResponse<number>, any>({
      query: () => {
        return {
          url: `admin/sales/new-purchase/user-count`,
          method: 'GET',
        }
      },
    }),

    getSalesGraphNewUserGrowthByGroup: builder.query<
      AppResponse<SalesGraphData[]>,
      {groupBy: SalesGroupByEnum}
    >({
      query: (params) => {
        return {
          url: `admin/sales/new-purchase/growth-by-group`,
          method: 'GET',
          params: params,
        }
      },
    }),

    getSalesGraphNewUserCountByGroup: builder.query<
      AppResponse<SalesGraphData[]>,
      {groupBy: SalesGroupByEnum}
    >({
      query: (params) => {
        return {
          url: `admin/sales/new-purchase/user-count-by-group`,
          method: 'GET',
          params: params,
        }
      },
    }),

    // approval
    bulkApprove: builder.mutation<
      AppResponse<any>,
      {notificationIds: string[]; status: 'approve' | 'reject'}
    >({
      query: (payload) => {
        return {
          url: `admin/bulk-approve`,
          method: 'POST',
          body: payload,
        }
      },
    }),

    // admin report activity log
    adminReportActivityLog: builder.query<
      AppResponse<PaginateResponse<MessageReportItems>>,
      AdminReportFilter
    >({
      query: (params) => {
        return {
          url: `admin/report/activity-log`,
          method: 'GET',
          params: params,
        }
      },
    }),

    adminReportUserList: builder.query<any, any>({
      query: () => {
        return {
          url: `admin/report/user-list`,
          method: 'GET',
        }
      },
    }),

    getRawContactPublic: builder.query<AppResponse<any>, {id: string}>({
      query: (payload) => {
        return {
          url: `public/raw-contacts/${payload.id}`,
          method: 'GET',
        }
      },
    }),

    updateFlow: builder.mutation<AppResponse<Flow>, {id: string; payload: FlowPayload}>({
      query: ({id, payload}) => ({
        url: `sms-flow/${id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    updateFlowStatus: builder.mutation<AppResponse<Flow>, {id: string; status: FlowStatus}>({
      query: ({id, status}) => ({
        url: `sms-flow/${id}/status`,
        method: 'PUT',
        body: {status},
      }),
    }),

    updateFlowSettings: builder.mutation<
      AppResponse<Flow>,
      {id: string; settings: Record<string, unknown>}
    >({
      query: ({id, settings}) => ({
        url: `sms-flow/${id}/settings`,
        method: 'PUT',
        body: {settings},
      }),
    }),

    getCreditRequired: builder.mutation<
      AppResponse<CreditCheck>,
      {id: string; nodes: Node<unknown>[]}
    >({
      query: ({id, nodes}) => ({
        url: `sms-flow/${id}/credit-estimate`,
        method: 'POST',
        body: {nodes},
      }),
    }),

    downloadRawContactPublic: builder.query<Blob, {id: string}>({
      query: (payload) => ({
        url: `public/raw-contacts/${payload.id}/contact/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    getFlowReport: builder.query<AppResponse<FlowReport>, {id: string; dateRange?: string}>({
      query: ({id, dateRange}) => ({
        url: `sms-flow/report/${id}`,
        method: 'GET',
        params: dateRange ? {dateRange} : undefined,
      }),
    }),

    getFlowReportTemplate: builder.query<
      AppResponse<{
        templateMessages: FlowTemplateStats[]
      }>,
      {id: string; dateRange?: string}
    >({
      query: ({id, dateRange}) => ({
        url: `sms-flow/report/${id}/template`,
        method: 'GET',
        params: dateRange ? {dateRange} : undefined,
      }),
    }),

    getFlowLogs: builder.query<
      AppResponse<PaginateResponse<IFlowLog>>,
      {page: number; limit: number; field?: string; sort?: string}
    >({
      query: (payload) => ({
        url: `sms-flow/logs`,
        method: 'GET',
        params: {
          page: payload.page,
          limit: payload.limit,
          ...(payload.field && {field: payload.field}),
          ...(payload.sort && {sort: payload.sort}),
        },
      }),
    }),

    getInstanceLogs: builder.query<
      AppResponse<PaginateResponse<IFlowInstance>>,
      {page: number; limit: number; id: string; field?: string; sort?: string}
    >({
      query: ({page, limit, id, field, sort}) => ({
        url: `sms-flow/logs/instances/${id}`,
        method: 'GET',
        params: {page, limit, ...(field && {field}), ...(sort && {sort})},
      }),
    }),

    getInstanceLogsDetail: builder.query<AppResponse<IFlowInstance>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}`,
        method: 'GET',
      }),
    }),

    getInstanceLogsStatus: builder.query<AppResponse<IFlowStatisticsSummary>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/logs/instances/${id}/status`,
        method: 'GET',
      }),
    }),

    pauseInstance: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}`,
        method: 'PUT',
        body: {status: LOGS_STATUS.PAUSED},
      }),
    }),

    resumeInstance: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}`,
        method: 'PUT',
        body: {status: LOGS_STATUS.PENDING},
      }),
    }),

    cancelInstance: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}`,
        method: 'PUT',
        body: {status: LOGS_STATUS.CANCELLED},
      }),
    }),

    deleteInstance: builder.mutation<AppResponse<{message: string}>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}`,
        method: 'DELETE',
      }),
    }),

    // /instances/:id/get-credit-required
    getCreditRequiredInstance: builder.query<AppResponse<CreditCheck>, {id: string}>({
      query: ({id}) => ({
        url: `sms-flow/instances/${id}/credit-estimate-instance`,
        method: 'GET',
      }),
    }),

    // Coupon
    getCouponList: builder.query<AppResponse<PaginateResponse<Coupons>>, CouponFilter>({
      query: (params) => {
        return {
          url: `admin/coupon/list`,
          method: 'GET',
          params: params,
        }
      },
    }),

    createCoupon: builder.mutation<AppResponse<Coupons>, {payload: CouponPayload}>({
      query: ({payload}) => ({
        url: `admin/coupon/create`,
        method: 'POST',
        body: payload,
      }),
    }),

    updateCoupon: builder.mutation<AppResponse<Coupons>, {id: string; payload: CouponPayload}>({
      query: ({id, payload}) => ({
        url: `admin/coupon/${id}`,
        method: 'PATCH',
        body: payload,
      }),
    }),

    getAccessToProduct: builder.query<ProductAccessResponse, {productId: string}>({
      query: ({productId}) => ({
        url: `products/${productId}/access`,
        method: 'GET',
      }),
    }),

    getProductPlan: builder.query<ProductPlanResponse, {productId: string}>({
      query: ({productId}) => ({
        url: `products/${productId}/plans`,
        method: 'GET',
      }),
    }),

    getSalesCommissionList: builder.query<AppResponse<SalesSummaryReport>, CommissionFilter>({
      query: (params) => {
        return {
          url: `user/commission/list`,
          method: 'GET',
          params: params,
        }
      },
    }),

    exportUserOrderAdminList: builder.query<
      any,
      {userId: string; fromDate: string; toDate: string}
    >({
      query: ({...params}) => {
        return {
          url: `user/commission/export`,
          method: 'GET',
          params,
        }
      },
    }),

    getUserOrderAdminList: builder.query<
      UserOrderAdminListResponse,
      {userId: string; fromDate: string; toDate: string; page: number; size: number}
    >({
      query: ({userId, ...params}) => {
        return {
          url: `user/get-user-orders-admin-list/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),

    getUserOrderAdminSummary: builder.query<
      UserOrderAdminSummaryResponse,
      {userId: string; fromDate: string; toDate: string}
    >({
      query: ({userId, ...params}) => {
        return {
          url: `user/get-user-orders-admin-summary/${userId}`,
          method: 'GET',
          params,
        }
      },
    }),
    purchaseProductPlan: builder.mutation<
      AppResponse<{message: string}>,
      PurchaseProductPlanRequest
    >({
      query: ({productId, planId, card, priceId, token, isTrial}) => ({
        url: `products/${productId}/${planId}/purchase`,
        method: 'POST',
        body: {
          card,
          priceId,
          token,
          productId,
          isTrial,
        },
      }),
    }),

    cancelSubscription: builder.mutation<
      AppResponse<{message: string}>,
      {stripeSubscriptionId: string; selectedReasonIds: string[]; customReason: string}
    >({
      query: ({stripeSubscriptionId, selectedReasonIds, customReason}) => ({
        url: `products/cancel/${stripeSubscriptionId}`,
        method: 'POST',
        body: {
          stripeSubscriptionId,
          selectedReasonIds,
          customReason,
        },
      }),
    }),

    getProductQuota: builder.query<AppResponse<ProductQuotaResponseData>, {productId: string}>({
      query: ({productId}) => ({
        url: `products/${productId}/quota`,
        method: 'GET',
      }),
    }),

    getSubscription: builder.query<
      AppResponse<ProductSubscriptionResponseData>,
      {productId?: string}
    >({
      query: ({productId}) => ({
        url: `products/subscriptions`,
        method: 'GET',
        params: {productId},
      }),
    }),

    getSubscriptionsOfUser: builder.query<AppResponse<ProductSubscriptionResponseData>, null>({
      query: () => ({
        url: `products/subscriptions-list`,
        method: 'GET',
      }),
    }),

    getUserSubscriptions: builder.query<
      AppResponse<ProductSubscriptionResponseData>,
      {userId: string}
    >({
      query: ({userId}) => ({
        url: `products/admin/subscriptions-list`,
        method: 'GET',
        body: {userId},
      }),
    }),

    getProduct: builder.query<
      AppResponse<{
        product: Product
      }>,
      {productKey: string}
    >({
      query: ({productKey}) => ({
        url: `products/${productKey}`,
        method: 'GET',
      }),
    }),

    getPaymentMethods: builder.query<AppResponse<PaymentMethodCard[]>, null>({
      query: () => ({
        url: `products/customer/payment-methods`,
        method: 'GET',
      }),
    }),

    addPaymentMethod: builder.mutation<AppResponse<{id: string}>, {token?: string; card?: string}>({
      query: (body) => ({
        url: `products/customer/payment-methods`,
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [],
    }),

    getUpcomingInvoiceChangePlan: builder.query<
      UpcomingInvoiceChangePlan,
      {
        currentPlanId: string | null
        newPlanId: string | null
        applyNow: boolean
        planId: string | null
        previewType: string
        isTrial: boolean
      }
    >({
      query: ({currentPlanId, newPlanId, applyNow = false, planId, previewType, isTrial}) => ({
        url: `products/subscription/change-plan/preview`,
        method: 'POST',
        body: {
          currentPlanId,
          newPlanId,
          applyNow,
          planId,
          previewType,
          isTrial,
        },
      }),
    }),

    getCancelReasons: builder.query<CancelReason, null>({
      query: () => ({
        url: `products/cancel-reasons`,
        method: 'GET',
      }),
    }),

    getInvoices: builder.query<
      AppResponse<InvoiceListResponse>,
      {limit?: number; starting_after?: string; ending_before?: string} | null
    >({
      query: (params) => ({
        url: `products/invoices`,
        method: 'GET',
        params: params ?? undefined,
      }),
      serializeQueryArgs: ({endpointName}) => endpointName,
      merge: (currentCache, newItems) => Object.assign(currentCache, newItems),
      forceRefetch({currentArg, previousArg}) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
    }),

    getTrialStatus: builder.query<
      AppResponse<{
        productId: string
        trialStatus: {
          canStartTrial: boolean
          hasUsedTrial: boolean
          isOnTrial: boolean
        }
      }>,
      {productId: string}
    >({
      query: ({productId}) => ({
        url: `products/${productId}/trial-status`,
        method: 'GET',
      }),
    }),

    changePlan: builder.mutation<
      AppResponse<{message: string}>,
      {subscriptionId: string; planId: string; applyNow: boolean}
    >({
      query: ({subscriptionId, planId, applyNow}) => ({
        url: `products/${subscriptionId}/${planId}/change-plan`,
        method: 'POST',
        body: {
          applyNow,
        },
      }),
    }),

    recoverSubscription: builder.mutation<AppResponse<{message: string}>, {subscriptionId: string}>(
      {
        query: ({subscriptionId}) => ({
          url: `products/recover/${subscriptionId}`,
          method: 'POST',
        }),
      }
    ),

    createSubscriptionByAdmin: builder.mutation<
      AppResponse<{message: string}>,
      {productId: string; planId: string; trialDays: number; userId: string}
    >({
      query: ({productId, planId, trialDays, userId}) => ({
        url: `products/admin/subscription/create`,
        method: 'POST',
        body: {productId, planId, trialDays, userId},
      }),
    }),

    updateSubscriptionByAdmin: builder.mutation<
      AppResponse<{message: string}>,
      {stripeSubscriptionId: string; trialDays: number; planId: string}
    >({
      query: ({stripeSubscriptionId, trialDays, planId}) => ({
        url: `products/admin/subscription/${stripeSubscriptionId}/update`,
        method: 'POST',
        body: {trialDays, planId},
      }),
    }),

    removeSubscriptionByAdmin: builder.mutation<
      AppResponse<{message: string}>,
      {stripeSubscriptionId: string}
    >({
      query: ({stripeSubscriptionId}) => ({
        url: `products/admin/subscription/${stripeSubscriptionId}/remove`,
        method: 'DELETE',
      }),
    }),

    // Report V2 - Dashboard Owner
    getTopPerformingCampaignsCtr: builder.query<
      AppResponse<any[]>,
      {fromDate?: string; toDate?: string; limit?: number; campType?: string}
    >({
      query: (params) => ({
        url: `report/top-performing-campaigns-ctr`,
        method: 'GET',
        params,
      }),
    }),

    getContactGrowthOverview: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>(
      {
        query: (params) => ({
          url: `report/contact-growth-overview`,
          method: 'GET',
          params,
        }),
      }
    ),

    getCostSavings: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/cost-savings`,
        method: 'GET',
        params,
      }),
    }),

    getOptOutsOverview: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/opt-outs-overview`,
        method: 'GET',
        params,
      }),
    }),

    getEngagementTrend: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/engagement-trend`,
        method: 'GET',
        params,
      }),
    }),

    getCommunicationMetrics: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/communication-metrics`,
        method: 'GET',
        params,
      }),
    }),

    getTopLeadSources: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/top-lead-sources`,
        method: 'GET',
        params,
      }),
    }),

    getMissedOpportunities: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/missed-opportunities`,
        method: 'GET',
        params,
      }),
    }),

    getTimeSaving: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/time-saving`,
        method: 'GET',
        params,
      }),
    }),

    getConversionRateSales: builder.query<AppResponse<any>, {fromDate?: string; toDate?: string}>({
      query: (params) => ({
        url: `report/conversion-rate-sales`,
        method: 'GET',
        params,
      }),
    }),
  }),
})

export const {
  // contact hooks
  useGetContactsQuery,
  useLazyGetContactsQuery,
  useGetContactCountQuery,
  useLazyGetContactInformationQuery,
  useLazyGetContactInformationMessageQuery,
  useLazyGetContactInformationClickThroughQuery,
  useLazyGetContactCountQuery,
  useExportContactMutation,
  useDeleteContactMutation,
  useUpdateContactFieldsMutation,

  // sub account hooks
  useGetSubClientsQuery,
  useGetSubClientQuery,
  useGetCountMessageQuery,
  useLazyGetSubClientsQuery,
  useLazyGetSubClientQuery,
  useLazyLoginAsSubClientQuery,
  useLazyLoginBackToSubClientQuery,
  useLazyGetSubClientCreditOverviewQuery,
  useLazyGetCountMessageQuery,
  useLazyGetSubClientSimpleQuery,
  useLazyGetSubClientTransactionHistoryQuery,
  useLazyGetSubClientTokensQuery,
  useLazyGetSubClientRemainingCreditQuery,
  useCreateSubClientMutation,
  useUpdateSubClientMutation,
  useDeleteSubClientMutation,
  useUpdateSubClientPasswordMutation,
  useUpdateSubClientStatusMutation,
  useAllocateCreditSubClientMutation,
  useActivateSubClientMutation,
  useDeactivateSubClientMutation,
  useCreateSubClientTokensMutation,
  useUpdateSubClientTokenStatusMutation,
  useDeleteSubClientTokenStatusMutation,
  useSetSubClientWebhookMutation,
  useSetSubClientCreditLimitMutation,
  useLazyGetAllocatedSubClientDedicatedNumbersQuery,
  useAllocateSubClientDedicatedNumberMutation,
  useRemoveAllocatedSubClientDedicatedNumberMutation,

  // --- sub client business number
  useLazyGetAllocatedSubClientBusinessNumbersQuery,
  useAllocateSubClientBusinessNumberMutation,
  useRemoveAllocatedSubClientBusinessNumberMutation,

  // --- sub client custom number
  useLazyGetAllocatedSubClientCustomNumbersQuery,
  useAllocateSubClientCustomNumberMutation,
  useRemoveAllocatedSubClientCustomNumberMutation,

  // upload client
  usePutUploadContactsMutation,
  usePutUploadCustomContactsMutation,

  // admin
  useLazyGetTotalSpendCreditQuery,
  useAdminRemoveCreditMutation,
  useGetAccountTypeChangeRequestQuery,
  useUpdateAccountTypeChangeRequestMutation,
  useUpdatePostPaidCreditLimitMutation,
  useUpdatePostPaidFlateRatePriceMutation,
  useGetCreditUsagePerMonthQuery,
  useChangeCreditStatusMutation,
  useCreateStaffCodeMutation,
  useRemoveStaffCodeMutation,
  useAdminReportActivityLogQuery,
  useLazyAdminReportActivityLogQuery,
  useAdminReportUserListQuery,

  // contact list
  useLazyContactGetListQuery,
  useLazyContactDetailListQuery,
  useLazyContactCountListQuery,
  useContactCreateListMutation,
  useContactUpdateListMutation,
  useContactDeleteListMutation,
  useGetNewUserCountQuery,

  //inbox
  useInboxReadAllMutation,
  useInboxCloseAllMutation,
  useGenerateHustleFollowupMutation,

  // bm
  useCancelCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,

  // message report
  useLazyAllMessageReportQuery,
  useLazyAllMessageCreditUsageReportQuery,
  useLazyGetMessageCreditUsageReportExportQuery,
  useLazyQuickMessageReportQuery,
  useLazyEmailToSmsReportQuery,
  useLazyQuickApiMessageReportQuery,
  useLazyFileSchedulerMessageReportQuery,
  useLazyBulkMessageShortLinkBarChartQuery,
  useLazyAnalyticShortLinkReportQuery,
  useLazyAnalyticShortLinkReportExportQuery,
  useLazyLandingPageShortLinkReportQuery,
  useLazyLandingPageShortLinkReportExportQuery,
  useLazyQuickApiMessageCreditUsageReportQuery,
  useLandingPageReportChartQuery,
  useLazyGetStaggerMessagesQuery,
  useLazyGetApiMessageExportQuery,
  useLazyAllMessageReportExportQuery,
  useLazySendReportToEmailQuery,
  useLazyAnalyticShortLinkCountByCountryQuery,
  useLazyGetBulkMatrixReportQuery,
  useLazyAnalyticShortLinkCountByCityQuery,

  // short links master
  useLazyGetShortLinksQuery,
  useLazyGetShortLinkDetailExportQuery,
  useLazyGetShortLinksExportQuery,

  // multi step automation
  useLazyGetListMultiStepAutomationQuery,
  useLazyGetDetailMultiStepAutomationQuery,
  useCreateMultiStepAutomationMutation,
  useUpdateMultiStepAutomationMutation,
  useDeleteMultiStepAutomationMutation,

  // referral
  useAdminSetCommissionRateMutation,
  useAffiliateChangeAccessMutation,
  useAffiliateResponseAccessMutation,
  useGenerateReferralCodeMutation,
  useLoginAsAffiliateUserMutation,
  useLoginAsParentAffiliateUserMutation,
  useSubmitReferralCodeMutation,
  useSubmitReferralCodeApprovalMutation,
  useUserRequestAccessMutation,
  useLazyGetAffiliateQuery,
  useLazyGetAffiliateCountQuery,
  useLazyGetCommissionsQuery,
  useLazyGetCommissionAmountQuery,
  useLazyAdminGetAffiliateQuery,
  useLazyAdminGetAffiliateCountQuery,
  useLazyAdminGetUserCommissionsQuery,
  useLazyAdminGetUserCommissionAmountQuery,

  // fraud keywords
  useLazyAdminGetFraudKeywordQuery,
  useAdminUpdateFraudKeywordMutation,

  // senders
  useLazyGetSendersQuery,

  // smpp
  useSmppAdminActivateMutation,
  useSmppAdminDeactivateMutation,
  useSmppAdminChangePasswordMutation,
  useSmppUserChangePasswordMutation,

  // archive
  useLazyExportArchiveMessageQuery,
  useLazyIsHaveArchiveDataQuery,

  // tasks
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useGetTasksByContactIdQuery,
  useLazyGetTasksByContactIdQuery,
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useCompleteTaskMutation,
  useCompleteMultipleTasksMutation,
  useDeleteTaskMutation,
  useDeleteMultipleTasksMutation,
  useGetTaskStatsQuery,
  useLazyGetTaskStatsQuery,

  // contact form
  useLazyGetContactFormsQuery,
  useLazyGetContactFormQuery,
  useLazyGetContactFormActiveQuery,
  useCreateContactFormMutation,
  useUpdateContactFormMutation,
  useDeleteContactFormMutation,

  // wallet card
  useLazyGetWalletCardQuery,
  useCreateWalletCardMutation,
  useLazyGetWalletCardListQuery,
  useUpdateWalletCardMutation,
  useDeleteWalletCardMutation,
  useLazyGetWalletCardPublicQuery,
  useCreateWalletCardPublicMutation,
  useDownloadWalletCardPublicQuery,
  useDownloadContactPublicQuery,
  useLazyGetWalletCardReportQuery,
  useDownloadWalletCardPublicGoogleQuery,
  useCreateWalletCardDraftMutation,
  useUpdateWalletCardDraftMutation,
  useGetWalletCardTemplateListSelectQuery,

  // wallet card template
  useLazyGetWalletCardTemplateQuery,
  useCreateWalletCardTemplateMutation,
  useUpdateWalletCardTemplateMutation,
  useDeleteWalletCardTemplateMutation,
  useCreateWalletCardTemplateDraftMutation,
  useUpdateWalletCardTemplateDraftMutation,
  useLazyGetWalletCardTemplateDetailQuery,
  // promo action
  useLazyGetPromoActionListQuery,
  useAddPromoActionMutation,
  useUpdatePromoActionMutation,

  useLazyGetVehicleTypeOptionsQuery,
  useCreateVehicleTypeOptionMutation,
  useUpdateVehicleTypeOptionMutation,
  useDeleteVehicleTypeOptionMutation,
  useLazyGetVehicleTypeOptionsPublicQuery,


  // raw contact
  useLazyGetRawContactListQuery,
  useExportRawContactListMutation,
  useCreateRawContactMutation,
  useAddRawContactMutation,
  useDeleteRawContactMutation,
  useUpdateRawContactSaleMutation,
  useAddTagtoRowContactMutation,
  useUpdateWelcomeMessageSentMutation,
  useUpdateWalletCardPublicMutation,
  useGetRawContactDetailQuery,
  useUploadVoiceMutation,
  useGetRecordListQuery,
  useDeleteVoiceMutation,
  useUpdateVoiceMutation,

  // sales
  useLazyGetSalesNewPurchaseQuery,
  useLazyGetSalesAllPurchaseQuery,
  useLazyGetSalesNewUserCountQuery,
  useLazyGetSalesNewUserCountInRangeQuery,
  useLazyGetSalesGraphNewUserGrowthByGroupQuery,
  useLazyGetSalesGraphNewUserCountByGroupQuery,
  useLazyExportSalesAllPurchaseQuery,

  // approval
  useBulkApproveMutation,
  // feature flow
  useGetSavedSequencesQuery,
  useDeleteSavedSequenceMutation,

 // flow
  useGetFlowsQuery,
  useCreateFlowMutation,
  useDeleteFlowMutation,
  useGetFlowQuery,
  useUpdateFlowMutation,
  useUpdateFlowStatusMutation,
  useUpdateFlowSettingsMutation,
  useGetCreditRequiredMutation,
  useGetFlowStatsQuery,

  useLazyGetWalletCardReportStackedQuery,
  useLazyGetWalletCardReportChartDetailQuery,
  useGetFlowReportQuery,
  useGetFlowReportTemplateQuery,
  useGetFlowLogsQuery,
  useGetInstanceLogsQuery,
  useGetInstanceLogsDetailQuery,
  useGetInstanceLogsStatusQuery,
  usePauseInstanceMutation,
  useResumeInstanceMutation,
  useDeleteInstanceMutation,
  useCancelInstanceMutation,
  useGetFlowPausedQuery,
  useResumeFlowPausedMutation,
  useGetCreditRequiredInstanceQuery,

  // SubClient Default Number Management (Parent Account)
  useSetSubClientDefaultDedicatedNumberMutation,
  useSetSubClientDefaultCustomNumberMutation,
  useSetSubClientDefaultBusinessNumberMutation,

  // SubClient Self-Management Default Numbers
  useSetSubClientOwnDefaultDedicatedNumberMutation,
  useSetSubClientOwnDefaultCustomNumberMutation,
  useSetSubClientOwnDefaultBusinessNumberMutation,

  // Coupon
  useLazyGetCouponListQuery,
  useCreateCouponMutation,
  useUpdateCouponMutation,

  // Commision
  useLazyGetSalesCommissionListQuery,
  useLazyExportUserOrderAdminListQuery,
  useGetUserOrderAdminListQuery,
  useGetUserOrderAdminSummaryQuery,

  // Product Access
  useGetAccessToProductQuery,
  useGetProductPlanQuery,
  usePurchaseProductPlanMutation,
  useCancelSubscriptionMutation,
  useGetProductQuotaQuery,
  useGetSubscriptionQuery,
  useGetUserSubscriptionsQuery,
  useGetProductQuery,
  useGetPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useGetUpcomingInvoiceChangePlanQuery,
  useGetCancelReasonsQuery,
  useGetInvoicesQuery,
  useGetTrialStatusQuery,
  useChangePlanMutation,
  useRecoverSubscriptionMutation,
  useCreateSubscriptionByAdminMutation,
  useUpdateSubscriptionByAdminMutation,
  useRemoveSubscriptionByAdminMutation,

  // Report V2 - Dashboard Owner
  useGetTopPerformingCampaignsCtrQuery,
  useGetContactGrowthOverviewQuery,
  useGetCostSavingsQuery,
  useGetOptOutsOverviewQuery,
  useGetEngagementTrendQuery,
  useGetCommunicationMetricsQuery,
  useGetTopLeadSourcesQuery,
  useGetMissedOpportunitiesQuery,
  useGetTimeSavingQuery,
  useGetConversionRateSalesQuery,

  // activity log
  useGetActivityLogByPhoneNumberQuery,
  useLazyGetActivityLogByPhoneNumberQuery,

  // notes timeline
  useGetNotesTimelineByPhoneNumberQuery,
  useLazyGetNotesTimelineByPhoneNumberQuery,
  useCreateNoteByPhoneNumberMutation,

  // raw contact by mobile (for AI summary)
  useGetRawContactByMobileQuery,
  useLazyGetRawContactByMobileQuery,
} = cellcastV2Api
